import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { selectIsCrmLabelEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmSettingsSelectors";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { openHooksModalV5 } from "app/services/ModalService";

import { StyledBaseCell, StyledInCRMButton } from "./styles";

type Props = {
    value?: string;
    type: CrmIntegrationProvider;
    onCellClick?: VoidFunction;
    onUpgradeClick?: VoidFunction;
};

export const InCRMCell = ({ value, type, onCellClick, onUpgradeClick }: Props) => {
    const { translate } = useTranslate();

    const isCrmLabelEnabled = useSelector((state) => selectIsCrmLabelEnabled(state, type));

    const isAdmin = salesSettingsHelper.isAdmin();

    const getTooltipText = () => {
        if (!isCrmLabelEnabled) {
            return isAdmin
                ? translate(`grow.lead_generator.exist.table.column.inCRM.tooltip.admin`, {
                      crm: getCrmName(type),
                  })
                : translate(`grow.lead_generator.exist.table.column.inCRM.tooltip`, {
                      crm: getCrmName(type),
                  });
        }

        return translate(`grow.lead_generator.exist.table.column.inCRM.${type}.tooltip`);
    };

    const handleClick = () => {
        window.open(value, "_blank");
        onCellClick?.();
    };

    const handleHookClick = () => {
        onUpgradeClick?.();
        openHooksModalV5("SalesSolutionLists");
    };

    const isCrmIntegrationsEnabled = salesSettingsHelper.isCrmIntegrationsEnabled();

    if (!isCrmIntegrationsEnabled) {
        return (
            <StyledInCRMButton
                type="flatTrial"
                iconName="sparkles"
                iconSize="xs"
                onClick={handleHookClick}
            >
                {translate("si.common.upgrade")}
            </StyledInCRMButton>
        );
    }

    if (!value) {
        return null;
    }

    return (
        <StyledBaseCell>
            <Tooltip content={<span dangerouslySetInnerHTML={{ __html: getTooltipText() }}></span>}>
                <div>
                    <IconButton
                        type="flat"
                        iconName={type}
                        iconSize="xs"
                        onClick={handleClick}
                        isDisabled={!isCrmLabelEnabled}
                    />
                </div>
            </Tooltip>
        </StyledBaseCell>
    );
};
